/**
 * The web app's calls to its own API: JSON in and out with the session cookie, and one error for
 * every refusal, `ApiError`, read from the services' error body (`readApiError`, CONVENTIONS.md,
 * « Errors »). A 401 is what `isUnauthorized` reads, for the query client's `sessionExpiry`.
 *
 *     export const api = apiClient({ base: "/api/app", writeHeader: "x-crowdrive" });
 *     const me = await api.get<Me>("/me");
 *     await api("/folders", { method: "POST", json: { name } });
 */

export type Query = Record<string, string | number | boolean | undefined | null>;

/** `path` with the query's set values (`undefined`, `null` and `""` are left out). */
export function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query))
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  const text = params.toString();
  return text ? `${path}?${text}` : path;
}

/** A refusal of the API: its HTTP status, the server's `error` code when it names one, its body. */
export class ApiError<Code extends string = string> extends Error {
  readonly status: number;
  readonly code: Code | null;
  readonly body: unknown;
  /** The invalid fields the server named (`error.issues`), `[]` without any. */
  readonly issues: readonly unknown[];

  constructor(status: number, code: Code | null, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
    this.issues = issuesOf(body);
  }
}

/** The session ended (or never was): an `ApiError` 401, with that `code` when one is given. */
export function isUnauthorized(error: unknown, code?: string): boolean {
  return error instanceof ApiError && error.status === 401 && (code === undefined || error.code === code);
}

/**
 * A refusal (an `ApiError`) read as `value`, other failures (the network) thrown still: for a call
 * whose refusal is an answer. `api.get(path).catch(refusedAs(null))`.
 */
export const refusedAs =
  <T>(value: T) =>
  (error: unknown): T => {
    if (error instanceof ApiError) return value;
    throw error;
  };

/** Whether a call succeeded: false for a refusal, a failure of the network thrown still. */
export const succeeded = (call: Promise<unknown>): Promise<boolean> => call.then(() => true, refusedAs(false));

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

const text = (value: unknown): string | null => (typeof value === "string" && value.length > 0 ? value : null);

function issuesOf(body: unknown): unknown[] {
  const issues = record(record(body)?.["error"])?.["issues"];
  return Array.isArray(issues) ? issues : [];
}

/** What a refusal's body says (`readApiError`). */
export interface ApiErrorReading {
  code: string | null;
  message: string | null;
  /** `error.issues`, `[]` without any. */
  issues: unknown[];
  /** The parsed JSON; `undefined` for a body that is not JSON. */
  body: unknown;
}

function readParsed(body: unknown): ApiErrorReading {
  const value = record(body);
  const error = value?.["error"];
  const envelope = record(error);
  if (envelope)
    return { code: text(envelope["code"]), message: text(envelope["message"]), issues: issuesOf(body), body };
  // Former formats: `{ error: CODE, message }`, `{ error: "<message>", code }`, RFC 6749's
  // `{ error, error_description }`, `{ statusCode, error, message }`, `{ error: "<code or message>" }`.
  const code = text(value?.["code"]) ?? text(error);
  const message = text(value?.["message"]) ?? text(value?.["error_description"]) ?? text(error);
  return { code, message, issues: [], body };
}

function readText(raw: string): ApiErrorReading {
  let body: unknown;
  try {
    body = JSON.parse(raw);
  } catch {
    const plain = raw.trim();
    const message = plain.length > 0 && plain.length <= 500 && !plain.startsWith("<") ? plain : null;
    return { code: null, message, issues: [], body: undefined };
  }
  return readParsed(body);
}

/**
 * What a refusal says, from its `Response` (its body read), its body's text or its parsed JSON:
 * the services' format, `{ error: { code, message, issues? } }` (CONVENTIONS.md, « Errors »), and
 * the former ones still understood while every service moves to it: `{ ok: false, error: { code,
 * message, issues } }` (crowlink, crowdex), `{ error: CODE, message }` (crowpus),
 * `{ error: "<message>", code }` (crowroom), `{ error: "<code or message>" }` (crowbox), RFC 6749's
 * `{ error, error_description }`, or plain text (a proxy's page is not a message).
 */
export function readApiError(response: Response): Promise<ApiErrorReading>;
export function readApiError(body: unknown): ApiErrorReading;
export function readApiError(input: unknown): ApiErrorReading | Promise<ApiErrorReading> {
  if (typeof Response !== "undefined" && input instanceof Response)
    return input.text().then(readText, () => readText(""));
  if (typeof input === "string") return readText(input);
  return readParsed(input);
}

/** `readApiError` of a body's text, without its issues (kept for its callers). */
export function readRefusal(raw: string): { code: string | null; message: string | null; body: unknown } {
  const { code, message, body } = readText(raw);
  return { code, message, body };
}

/** How a refusal becomes an error, when its body does not say everything. */
export interface RefusalOptions {
  /** The code of a refusal whose body names none. Default none (`null`). */
  fallbackCode?(status: number): string | null;
  /** The message of a refusal whose body has none. Default `HTTP <status>`. */
  fallbackMessage?(status: number, statusText: string): string;
  /** The error thrown, an `ApiError` or a subclass of it (crowdex's `ApiClientError`). */
  createError?(status: number, code: string | null, message: string, body: unknown): ApiError;
}

/**
 * The `ApiError` of a refusal, from its status and its body's text (`readApiError`): for the
 * calls `apiClient` does not make, such as an `XMLHttpRequest` upload reporting its progress.
 */
export function refusalError(
  status: number,
  body: string,
  options: RefusalOptions & { statusText?: string } = {},
): ApiError {
  const read = readText(body);
  const code = read.code ?? options.fallbackCode?.(status) ?? null;
  const message = read.message ?? options.fallbackMessage?.(status, options.statusText ?? "") ?? `HTTP ${status}`;
  return options.createError?.(status, code, message, read.body) ?? new ApiError(status, code, message, read.body);
}

/**
 * The `ApiError` a refused `Response` stands for (its body read): for the fetches `apiClient`
 * does not make (a download, a stream, a file's content, another origin's API).
 */
export async function responseError(response: Response, options: RefusalOptions = {}): Promise<ApiError> {
  const body = await response.text().catch(() => "");
  return refusalError(response.status, body, { ...options, statusText: response.statusText });
}

/** `response` when it succeeded, else throws its `responseError`. */
export async function okResponse(response: Response, options: RefusalOptions = {}): Promise<Response> {
  if (!response.ok) throw await responseError(response, options);
  return response;
}

export interface ApiClientOptions extends RefusalOptions {
  /** Prefix of every path (`/api/app`). */
  base?: string;
  /** Header sent as `1` with every write (not a GET): the server's guard against forged requests. */
  writeHeader?: string;
  /** Headers of every call, under the call's own. */
  headers?: Record<string, string>;
  /** Default `same-origin`. */
  credentials?: RequestCredentials;
  /** The bodies are an `{ ok: true, data }` envelope (crowlink, crowdex): a call answers `data`. */
  envelope?: boolean;
  /** On a 401, before throwing (`redirectToSignin`): also for calls the query client does not make. */
  onUnauthorized?(): void;
}

/** A call: `fetch`'s init, plus a JSON body (`json`) and a query string (`query`). */
export type ApiInit = RequestInit & { json?: unknown; query?: Query };

export interface ApiClient {
  <T = unknown>(path: string, init?: ApiInit): Promise<T>;
  get<T = unknown>(path: string, query?: Query): Promise<T>;
  /** The body defaults to `{}`, as for `put` and `patch`. */
  post<T = unknown>(path: string, body?: unknown): Promise<T>;
  put<T = unknown>(path: string, body?: unknown): Promise<T>;
  patch<T = unknown>(path: string, body?: unknown): Promise<T>;
  del<T = unknown>(path: string): Promise<T>;
  /**
   * The call's `Response` once it succeeded, its body unread: a file, a stream (server-sent
   * events, a model's output), a body that is not JSON. A refusal throws, as for every call.
   */
  raw(path: string, init?: ApiInit): Promise<Response>;
}

/**
 * The client of one API. A call answers the response's JSON (`undefined` for an empty body, the
 * text for a body that is not JSON), and throws an `ApiError` for a refusal.
 */
export function apiClient(options: ApiClientOptions = {}): ApiClient {
  const { base = "", writeHeader, envelope = false, onUnauthorized } = options;

  const refusal = (response: Response, body: string): ApiError =>
    refusalError(response.status, body, { ...options, statusText: response.statusText });

  const send = async (path: string, init: ApiInit = {}): Promise<Response> => {
    const { json, query, headers: own, ...rest } = init;
    const method = (rest.method ?? "GET").toUpperCase();
    const headers = new Headers(options.headers);
    if (writeHeader && method !== "GET") headers.set(writeHeader, "1");
    if (json !== undefined) headers.set("content-type", "application/json");
    new Headers(own).forEach((value, key) => headers.set(key, value));
    const plain: Record<string, string> = {};
    headers.forEach((value, key) => {
      plain[key] = value;
    });
    return fetch(withQuery(`${base}${path}`, query), {
      credentials: options.credentials ?? "same-origin",
      ...rest,
      method,
      headers: plain,
      ...(json === undefined ? {} : { body: JSON.stringify(json) }),
    });
  };

  const refused = async (response: Response, body?: string): Promise<ApiError> => {
    const error = refusal(response, body ?? (await response.text().catch(() => "")));
    if (response.status === 401) onUnauthorized?.();
    return error;
  };

  const call = async <T>(path: string, init: ApiInit = {}): Promise<T> => {
    const response = await send(path, init);
    const body = response.status === 204 ? "" : await response.text().catch(() => "");
    if (!response.ok) throw await refused(response, body);
    if (!body) {
      if (envelope) throw refusal(response, body);
      return undefined as T;
    }
    let data: unknown;
    try {
      data = JSON.parse(body);
    } catch {
      if (envelope) throw refusal(response, "");
      return body as T;
    }
    if (!envelope) return data as T;
    const result = record(data);
    if (result?.["ok"] !== true) throw refusal(response, body);
    return result["data"] as T;
  };

  const raw = async (path: string, init?: ApiInit): Promise<Response> => {
    const response = await send(path, init);
    if (!response.ok) throw await refused(response);
    return response;
  };

  return Object.assign(call, {
    get: <T>(path: string, query?: Query) => call<T>(path, { query }),
    post: <T>(path: string, body?: unknown) => call<T>(path, { method: "POST", json: body ?? {} }),
    put: <T>(path: string, body?: unknown) => call<T>(path, { method: "PUT", json: body ?? {} }),
    patch: <T>(path: string, body?: unknown) => call<T>(path, { method: "PATCH", json: body ?? {} }),
    del: <T>(path: string) => call<T>(path, { method: "DELETE" }),
    raw,
  });
}
